<template>
  <div>
    <el-dialog
      :visible.sync="dialogVisible"
      :modal-append-to-body="false"
      :before-close="closeDialog"
      :title="
        id
          ? $t('Gamification_AddGameDialog_Title_Edit')
          : $t('Gamification_AddGameDialog_Title_Add')
      "
      :close-on-click-modal="false"
      @click.native.stop
      top="0"
    >
      <rest-form
        ref="form"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        v-loading="isLoading"
        :model="model"
        :rules="rules"
      >
        <el-form-item
          :label="$t('Gamification_AddGameForm_GameName')"
          prop="name"
        >
          <el-input maxlength="50" show-word-limit v-model="model.name" />
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_ShortDescription')"
          prop="description"
        >
          <el-input
            type="textarea"
            maxlength="255"
            show-word-limit
            :rows="5"
            v-model="model.description"
          />
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_LongDescription')"
          prop="long_description"
        >
          <el-input
            type="textarea"
            :rows="5"
            v-model="model.long_description"
          />
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_GameImage')"
          prop="image_url"
        >
          <el-upload
            class="image-uploader"
            action="#"
            :show-file-list="false"
            :http-request="handleUpload('image_url')"
          >
            <img
              v-if="model.image_url"
              :src="model.image_url"
              class="img"
              :alt="$t('Gamification_AddGameForm_GameImage_Alt')"
            />
            <i v-else class="el-icon-plus image-uploader-icon"></i>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <div class="segmentation-form-item">
            <span>{{ $t('Gamification_AddGameForm_DemoImageUrl') }}</span>
            <el-switch
              @change="clearValidate"
              :inactive-value="false"
              :active-value="true"
              v-model="chooseImage"
              active-color="#1d87c9"
            />
          </div>
        </el-form-item>
        <el-form-item
          v-if="chooseImage"
          :label="$t('Gamification_AddGameForm_DemoGameImage')"
          prop="demo_image_url"
        >
          <el-upload
            class="image-uploader"
            action="#"
            :show-file-list="false"
            :http-request="handleUpload('demo_image_url')"
          >
            <img
              v-if="model.demo_image_url"
              :src="model.demo_image_url"
              class="img"
              :alt="$t('Gamification_AddGameForm_GameImage_Alt')"
            />
            <i v-else class="el-icon-plus image-uploader-icon"></i>
          </el-upload>
        </el-form-item>
        <el-form-item
          v-else
          :label="$t('Gamification_AddGameForm_DemoGameUrl')"
          prop="demo_game_url"
        >
          <el-input v-model="model.demo_game_url" />
        </el-form-item>
        <el-form-item>
          <div class="segmentation-form-item">
            <span>{{ $t('Gamification_AddGameForm_Segmentation') }}</span>
            <el-switch v-model="model.segmentation" active-color="#1d87c9" />
          </div>
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_GameType')"
          prop="game_type"
        >
          <el-select
            v-model="model.game_type"
            multiple
            :placeholder="$t('Gamification_AddGameForm_SelectPlaceholder')"
          >
            <el-option
              v-for="(item, index) in GameTypeLabel"
              :label="$t(item)"
              :key="item"
              :value="index"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <div class="segmentation-form-item">
            <span>{{ $t('Gamification_AddGameForm_XpBackOffice') }}</span>
            <el-tooltip
              :content="getBackOfficeSwitchTooltip"
              :disabled="!disableXpBackofficeSwitch"
              placement="right"
            >
              <el-switch
                v-model="model.xp_backoffice_ui"
                active-color="#1d87c9"
                :disabled="disableXpBackofficeSwitch"
                @change="xpBackOfficeSwitchChange"
              />
            </el-tooltip>
          </div>
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_DashboardDomain')"
          prop="dashboard_domain"
          v-if="model.xp_backoffice_ui"
          :rules="{
            required: model.xp_backoffice_ui,
            message: $t('Gamification_AddGameForm_Regions_DomainRequired'),
            trigger: 'blur'
          }"
        >
          <el-input v-model="model.dashboard_domain" :disabled="isActiveGame" />
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_GameCampaignType')"
          prop="game_campaign_type"
          v-if="model.xp_backoffice_ui"
        >
          <el-select
            v-model="model.game_campaign_type"
            :placeholder="$t('Gamification_AddGameForm_SelectPlaceholder')"
            clearable
            :disabled="isActiveGame"
          >
            <el-option
              v-for="(item, index) in GameCampaignType"
              :label="$t(`Gamification_Game_${item}`)"
              :key="index"
              :value="Number(index)"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('Gamification_AddGameForm_Regions')"
          v-if="!model.xp_backoffice_ui"
        >
          <div class="regions-ct">
            <div
              class="region"
              v-for="(region, index) in model.gameRegions"
              :key="`region-${index}-${region.continent}`"
            >
              <el-form-item
                class="region-child"
                :prop="`gameRegions.${index}.continent`"
                :rules="{
                  required: true,
                  message: $t(
                    'Gamification_AddGameForm_Regions_ContinentRequired'
                  ),
                  trigger: 'blur'
                }"
              >
                <el-select
                  v-model="model.gameRegions[index].continent"
                  :placeholder="
                    $t(
                      'Gamification_AddGameForm_Regions_SelectContinentPlaceholder'
                    )
                  "
                >
                  <el-option
                    v-for="(item, regionLabelIndex) in RegionNameLabel"
                    :label="$t(item)"
                    :key="item"
                    :value="regionLabelIndex"
                  />
                </el-select>
              </el-form-item>
              <el-form-item
                class="region-child"
                :prop="`gameRegions.${index}.name`"
                :rules="{
                  required: true,
                  message: $t('Gamification_AddGameForm_Regions_NameRequired'),
                  trigger: 'blur'
                }"
              >
                <el-input
                  v-model="model.gameRegions[index].name"
                  :placeholder="
                    $t('Gamification_AddGameForm_Regions_NamePlaceholder')
                  "
                />
              </el-form-item>
              <el-form-item
                class="region-child"
                :prop="`gameRegions.${index}.dashboard_domain`"
                :rules="[
                  {
                    required: true,
                    message: $t(
                      'Gamification_AddGameForm_Regions_DomainRequired'
                    ),
                    trigger: 'blur'
                  },
                  {
                    validator: validateDashboardDomain,
                    trigger: 'change'
                  }
                ]"
              >
                <el-input
                  v-model="model.gameRegions[index].dashboard_domain"
                  :placeholder="
                    $t('Gamification_AddGameForm_Regions_DomainPlaceholder')
                  "
                />
              </el-form-item>
              <icon
                @click="removeRegionField(index)"
                class="region-remove-icon"
                glyph="bin"
              />
            </div>
            <span>
              <el-tooltip
                :content="
                  $t('Gamification_AddGameForm_BackOfficeRegionsBtnTooltip')
                "
                :disabled="!model.dashboard_domain.length"
              >
                <el-button
                  icon="el-icon-plus"
                  type="primary"
                  @click="addRegionField"
                  :disabled="!!model.dashboard_domain.length"
                >
                  {{ $t('Gamification_AddGameForm_Regions_AddRegionBtn') }}
                </el-button>
              </el-tooltip>
            </span>
          </div>
        </el-form-item>
        <div class="add-btn-ct">
          <el-button @click="save" icon="ico icon-floppy" type="primary">
            {{
              id
                ? $t('Gamification_AddGameForm_SaveGameBtn')
                : $t('Gamification_AddGameForm_AddGameBtn')
            }}
          </el-button>
        </div>
      </rest-form>
    </el-dialog>
  </div>
</template>

<script>
import { GameTypeLabel } from '@/modules/gamification/enum/GameTypeEnum'
import { RegionNameLabel } from '@/modules/gamification/enum/RegionNameEnum'
import { GameCampaignType } from '@/modules/gamification/enum/GameCampaignTypeEnum'
import { projectEndpoint, toBase64 } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import RestForm from '@/common/components/rest/RestForm.vue'

export default {
  name: 'AddGameDialog',
  components: { RestForm },
  props: {
    id: {
      type: Number,
      required: false
    },
    dialogVisible: {
      type: Boolean,
      required: true
    },
    gameStatus: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      isLoading: false,
      chooseImage: false,
      model: {
        name: '',
        description: '',
        long_description: '',
        image_url: '',
        demo_image_url: '',
        demo_game_url: '',
        segmentation: 0,
        game_type: [],
        gameRegions: [],
        dashboard_domain: '',
        xp_backoffice_ui: 0,
        game_campaign_type: null
      },
      GameCampaignType,
      GameTypeLabel,
      RegionNameLabel,
      rules: {
        name: [
          {
            required: true,
            message: this.$t('Gamification_AddGameForm_GameName_Required'),
            trigger: 'blur'
          }
        ],
        description: [
          {
            required: true,
            message: this.$t(
              'Gamification_AddGameForm_ShortDescription_Required'
            ),
            trigger: 'blur'
          }
        ],
        long_description: [
          {
            required: true,
            message: this.$t(
              'Gamification_AddGameForm_LongDescription_Required'
            ),
            trigger: 'blur'
          }
        ],
        image_url: [
          {
            required: true,
            message: this.$t('Gamification_AddGameForm_GameImage_Required'),
            trigger: ['blur', 'change']
          }
        ],
        demo_image_url: [
          {
            validator: (rule, value, callback) => {
              if (this.chooseImage && !this.model.demo_image_url) {
                callback(
                  new Error(
                    this.$t('Gamification_AddGameForm_DemoImageUrl_Required')
                  )
                )
              } else {
                callback()
              }
            },
            trigger: ['blur', 'change']
          }
        ],
        demo_game_url: [
          {
            validator: (rule, value, callback) => {
              if (!this.chooseImage && !this.model.demo_game_url) {
                callback(
                  new Error(
                    this.$t('Gamification_AddGameForm_DemoGameUrl_Required')
                  )
                )
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        game_type: [
          {
            required: true,
            message: this.$t('Gamification_AddGameForm_GameType_Required'),
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  mounted() {
    if (this.id) {
      this.isLoading = true
      RestApi(this.fetchEndpoint)
        .then(({ data }) => {
          this.model = data
          this.chooseImage = !!this.model.demo_image_url
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },
  computed: {
    saveEndpoint() {
      if (this.id) {
        return projectEndpoint({
          method: 'patch',
          url: `games/${this.id}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: `games`
        })
      }
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `games/${this.id}`,
        params: { expand: 'gameRegions' }
      })
    },
    hasGameRegions() {
      return this.model.gameRegions.length > 0
    },
    disableXpBackofficeSwitch() {
      return !!(
        this.hasGameRegions ||
        (this.model.dashboard_domain.length && !this.isInActiveGame) ||
        Number.isInteger(this.model.game_campaign_type) ||
        this.isActiveGame
      )
    },
    isActiveGame() {
      return this.gameStatus === 'active'
    },
    isInActiveGame() {
      return this.gameStatus === 'inactive'
    },
    getBackOfficeSwitchTooltip() {
      if (this.isActiveGame) {
        return this.$t(
          'Gamification_AddGameForm_BackOfficeRegions_ActiveGameTooltip'
        )
      }
      if (this.hasGameRegions) {
        return this.$t('Gamification_AddGameForm_BackOfficeRegionsTooltip')
      }
      if (
        this.model.dashboard_domain.length ||
        Number.isInteger(this.model.game_campaign_type)
      ) {
        return this.$t(
          'Gamification_AddGameForm_BackOfficeDashboardDomainToolTip'
        )
      }
    }
  },
  methods: {
    clearValidate() {
      this.$refs.form.validateField(['demo_image_url', 'demo_game_url'])
    },
    closeDialog() {
      this.$emit('close')
    },
    handleUpload(target) {
      return async option => {
        const encodedFile = await toBase64(option.file)
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `games/upload-image`,
            data: {
              file: encodedFile
            }
          })
        ).then(({ data }) => {
          this.model[target] = data
        })
      }
    },
    save() {
      if (this.chooseImage) {
        this.model.demo_game_url = null
      } else {
        this.model.demo_image_url = null
      }
      if (
        !this.model.xp_backoffice_ui &&
        this.model.game_campaign_type !== null
      ) {
        this.model.game_campaign_type = null
      }
      this.$refs['form'].save()
    },
    afterSave(data, finishLoading) {
      this.$store.dispatch('fetchGamesToPurchase').finally(() => {
        finishLoading.apply()
      })
      this.closeDialog()
    },
    addRegionField() {
      this.model.gameRegions.push({
        continent: '',
        name: '',
        dashboard_domain: ''
      })
    },
    removeRegionField(regionIndex) {
      this.model.gameRegions.splice(regionIndex, 1)
    },
    validateDashboardDomain(rule, value, callback) {
      const dashboardDomainSearch = this.model.gameRegions.filter(
        region => region.dashboard_domain === value
      )
      if (dashboardDomainSearch.length > 1) {
        callback(
          new Error(
            this.$t('Gamification_AddGameForm_Regions_DomainAlreadyInUse')
          )
        )
      }
      callback()
    },
    xpBackOfficeSwitchChange(event) {
      if (event === 0) {
        this.$refs.form.$refs.form.clearValidate([
          'dashboard_domain',
          'game_campaign_type'
        ])
      }
    }
  }
}
</script>

<style scoped>
>>> .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

>>> .el-dialog__body {
  height: 650px;
  overflow: auto;
}

>>> .el-switch__label.is-active {
  color: #409eff;
}

>>> .image-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.image-uploader .el-upload:hover {
  border-color: #409eff;
}

.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}

.img {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

.add-btn-ct {
  display: flex;
  justify-content: flex-end;
}

.regions-ct {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.region {
  display: flex;
  align-items: center;
  gap: 5px;
}

.region-child {
  flex: 1;
}

.segmentation-form-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.region-remove-icon {
  width: 20px;
  height: 20px;
  color: #a7acba;
  cursor: pointer;
}

@media screen and (max-height: 950px) {
  >>> .el-dialog__body {
    height: 550px;
  }
}

@media screen and (max-height: 850px) {
  >>> .el-dialog__body {
    height: 450px;
  }
}

@media screen and (max-height: 650px) {
  >>> .el-dialog__body {
    height: 250px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-hub-dashboard/AddGameDialog.vue